import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import api from '../api';
import AdvertisingCostsTable from '../components/AdvertisingCostsTable';
import './MerchantPage.css';

const MerchantPage = () => {
    const { user } = useAuth();
    const [googleStatus, setGoogleStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [merchants, setMerchants] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [showMerchantSelection, setShowMerchantSelection] = useState(false);
    const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
    const [selectedGoogleAdsAccount, setSelectedGoogleAdsAccount] = useState(null);
    const [showGoogleAdsSelection, setShowGoogleAdsSelection] = useState(false);
    const [acceptCSSTerms, setAcceptCSSTerms] = useState(false);
    const [prerequisites, setPrerequisites] = useState(null);

    useEffect(() => {
        loadGoogleStatus();
        loadPrerequisites();
    }, []);

    // Controlla se siamo nel callback di Google
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('success') === 'google_connected') {
            setError(null);
            loadGoogleStatus();
            loadPrerequisites();
            window.history.replaceState({}, document.title, '/merchant');
        } else if (urlParams.get('error')) {
            handleError(urlParams.get('error'));
            window.history.replaceState({}, document.title, '/merchant');
        }
    }, []);

    const loadGoogleStatus = async () => {
        try {
            setLoading(true);
            const response = await api.get('/merchant/google/status');
            setGoogleStatus(response.data);
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Errore nel caricamento stato Google';
            const errorType = err.response?.data?.errorType;
            
            if (errorType === 'oauth_expired' || errorType === 'oauth_invalid') {
                setError('Il token di accesso è scaduto o non valido. Riconnetti il tuo account Google dalla pagina del profilo.');
            } else {
                setError(errorMessage);
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadPrerequisites = async () => {
        try {
            const response = await api.get('/merchant/prerequisites');
            setPrerequisites(response.data);
        } catch (error) {
            console.error('Errore nel caricamento prerequisiti:', error);
            setError('Errore nel caricamento dei prerequisiti');
        }
    };

    const handleError = (errorCode) => {
        switch (errorCode) {
            case 'oauth_error':
                setError('Errore durante l\'autenticazione con Google. Riprova.');
                break;
            default:
                setError('Errore sconosciuto. Riprova.');
        }
    };

    const initiateGoogleAuth = async () => {
        try {
            setConnecting(true);
            const response = await api.get('/merchant/auth/google');
            window.location.href = response.data.authUrl;
        } catch (err) {
            setError('Errore nell\'inizializzazione OAuth');
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const disconnectGoogle = async () => {
        if (!window.confirm('Sei sicuro di voler scollegare il tuo account Google?')) return;

        try {
            await api.delete('/merchant/google/disconnect');
            setGoogleStatus(null);
            loadPrerequisites();
        } catch (err) {
            setError('Errore nello scollegamento Google');
            console.error(err);
        }
    };

    const loadMerchantList = async () => {
        try {
            setConnecting(true);
            const response = await api.get('/merchant/merchant/list');
            setMerchants(response.data.merchants || []);
            setShowMerchantSelection(true);
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Errore nel caricamento lista merchant';
            const errorType = err.response?.data?.errorType;
            
            if (errorType === 'oauth_expired' || errorType === 'oauth_invalid') {
                setError('Il token di accesso è scaduto o non valido. Riconnetti il tuo account Google dalla pagina del profilo.');
            } else if (errorType === 'developer_token_not_approved') {
                setError('Il developer token è configurato solo per account di test. Per accedere ai dati di produzione, richiedi l\'upgrade a Basic o Standard access.');
            } else {
                setError(errorMessage);
            }
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const connectMerchant = async () => {
        if (!selectedMerchant) return;

        try {
            setConnecting(true);
            await api.post('/merchant/merchant/connect', {
                merchantId: selectedMerchant.id,
                merchantName: selectedMerchant.name
            });
            
            setShowMerchantSelection(false);
            setSelectedMerchant(null);
            setMerchants([]);
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nel collegamento merchant');
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const disconnectMerchant = async () => {
        if (!window.confirm('Sei sicuro di voler scollegare questo merchant?')) return;

        try {
            await api.delete('/merchant/merchant/disconnect');
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nello scollegamento merchant');
            console.error(err);
        }
    };

    const requestCSS = async () => {
        if (!acceptCSSTerms) {
            setError('Devi accettare i termini per richiedere il passaggio a Loverlist CSS');
            return;
        }

        try {
            await api.post('/merchant/merchant/request-css');
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nella richiesta CSS');
            console.error(err);
        }
    };

    const verifyCSSIntegration = async () => {
        try {
            setConnecting(true);
            const response = await api.post('/merchant/merchant/verify-css');
            if (response.data.success) {
                setError(null);
            }
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nella verifica CSS');
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const loadGoogleAdsAccounts = async () => {
        try {
            setConnecting(true);
            const response = await api.get('/merchant/google-ads/accounts');
            setGoogleAdsAccounts(response.data.accounts || []);
            setShowGoogleAdsSelection(true);
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Errore nel caricamento account Google Ads';
            const errorType = err.response?.data?.errorType;
            
            if (errorType === 'oauth_expired' || errorType === 'oauth_invalid') {
                setError('Il token di accesso è scaduto o non valido. Riconnetti il tuo account Google Ads dalla pagina del profilo.');
            } else if (errorType === 'developer_token_not_approved') {
                setError('Il developer token è configurato solo per account di test. Per accedere ai dati di produzione, richiedi l\'upgrade a Basic o Standard access.');
            } else {
                setError(errorMessage);
            }
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const connectGoogleAds = async () => {
        if (!selectedGoogleAdsAccount) return;

        try {
            setConnecting(true);
            await api.post('/merchant/google-ads/connect', {
                customerId: selectedGoogleAdsAccount.customerId,
                customerName: selectedGoogleAdsAccount.name
            });
            
            setShowGoogleAdsSelection(false);
            setSelectedGoogleAdsAccount(null);
            setGoogleAdsAccounts([]);
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nel collegamento Google Ads');
            console.error(err);
        } finally {
            setConnecting(false);
        }
    };

    const disconnectGoogleAds = async () => {
        if (!window.confirm('Sei sicuro di voler scollegare questo account Google Ads?')) return;

        try {
            await api.delete('/merchant/google-ads/disconnect');
            loadGoogleStatus();
            loadPrerequisites();
        } catch (err) {
            setError('Errore nello scollegamento Google Ads');
            console.error(err);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            google_only: { label: 'Solo Google', className: 'badge-info' },
            connected: { label: 'Merchant Collegato', className: 'badge-success' },
            requested: { label: 'CSS Richiesto', className: 'badge-warning' },
            css_request_sent: { label: 'Richiesta CSS Inviata', className: 'badge-info' },
            accepted: { label: 'CSS Attivo', className: 'badge-success' },
            disconnected: { label: 'Scollegato', className: 'badge-danger' }
        };

        const config = statusConfig[status] || { label: status, className: 'badge-secondary' };
        return <span className={`badge ${config.className}`}>{config.label}</span>;
    };

    // Verifica se i prerequisiti di base sono soddisfatti
    const isBillingComplete = prerequisites?.prerequisites?.billing?.satisfied;
    const isPaymentComplete = prerequisites?.prerequisites?.paymentMethod?.satisfied;
    const isGoogleConnected = googleStatus?.connected;
    const isGoogleAdsConnected = googleStatus?.googleAdsAccount;
    const isMerchantConnected = googleStatus?.merchantAccount;

    // Mostra la spiegazione solo se fatturazione o pagamento non sono completi
    const shouldShowExplanation = !isBillingComplete || !isPaymentComplete;

    if (loading) {
        return (
            <div className="merchant-page">
                <div className="loading">Caricamento...</div>
            </div>
        );
    }

    return (
        <div className="merchant-page">
            <div className="container">
                <div className="page-header">
                    <h1>Trasferisci Merchant Center a Loverlist CSS</h1>
                    <p>Risparmia il 20% sui tuoi costi pubblicitari Google Shopping</p>
                </div>

                {error && (
                    <div className="alert alert-danger">
                        {error}
                        <button onClick={() => setError(null)} className="close-btn">&times;</button>
                    </div>
                )}

                {/* Sezione Spiegazione */}
                {shouldShowExplanation && (
                    <div className="explanation-section">
                        <div className="explanation-card">
                            <div className="explanation-content">
                                <h2>Perché passare a Loverlist CSS?</h2>
                                <div className="explanation-grid">
                                    <div className="explanation-item">
                                        <div className="explanation-icon">💰</div>
                                        <h3>Risparmio del 20% sui CPC</h3>
                                        <p>Elimina il markup del 20% che Google applica sui tuoi costi pubblicitari. Con Loverlist CSS aumenti la visibilità e competi al cpc effettivo.</p>
                                    </div>
                                    <div className="explanation-item">
                                        <div className="explanation-icon">🔄</div>
                                        <h3>Transizione trasparente</h3>
                                        <p>I tuoi annunci continuano a portare traffico sul tuo sito. Nei tuoi annunci shopping cambierà solo la scritta da "di Google" a "di Loverlist".</p>
                                    </div>
                                    <div className="explanation-item">
                                        <div className="explanation-icon">💡</div>
                                        <h3>Commissione competitiva</h3>
                                        <p>Loverlist trattiene il 5% + IVA della tua spesa pubblicitaria google shopping, garantendo un risparmio netto per le tue campagne del 15%.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sezione Informazioni di Fatturazione */}
                <div className="step-section">
                    <div className="step-header">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h2>Informazioni di Fatturazione</h2>
                            <p>Configura i dati aziendali per la fatturazione</p>
                        </div>
                    </div>
                    
                    <div className="step-card">
                        {isBillingComplete ? (
                            <div className="step-complete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">✓</span>
                                        <span className="status-text">Configurato</span>
                                    </div>
                                    <div className="step-details">
                                        <strong>{prerequisites?.prerequisites?.billing?.data?.business_name}</strong>
                                        <span>{prerequisites?.prerequisites?.billing?.data?.vat_number}</span>
                                    </div>
                                </div>
                                <a href="/billing" className="merchant-btn merchant-btn-outline">Modifica</a>
                            </div>
                        ) : (
                            <div className="step-incomplete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">!</span>
                                        <span className="status-text">Da configurare</span>
                                    </div>
                                    <div className="step-details">
                                        <span>Informazioni aziendali e dati fiscali richiesti</span>
                                    </div>
                                </div>
                                <a href="/billing" className="merchant-btn merchant-btn-primary">Configura Fatturazione</a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sezione Metodo di Pagamento */}
                <div className="step-section">
                    <div className="step-header">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h2>Metodo di Pagamento</h2>
                            <p>Configura il metodo di pagamento per le commissioni</p>
                        </div>
                    </div>
                    
                    <div className="step-card">
                        {isPaymentComplete ? (
                            <div className="step-complete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">✓</span>
                                        <span className="status-text">Configurato</span>
                                    </div>
                                    <div className="step-details">
                                        <strong>Metodo di pagamento attivo</strong>
                                        <span>{prerequisites?.prerequisites?.paymentMethod?.message}</span>
                                    </div>
                                </div>
                                <a href="/billing" className="merchant-btn merchant-btn-outline">Modifica</a>
                            </div>
                        ) : (
                            <div className="step-incomplete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">!</span>
                                        <span className="status-text">Da configurare</span>
                                    </div>
                                    <div className="step-details">
                                        <span>Metodo di pagamento richiesto per le commissioni</span>
                                    </div>
                                </div>
                                <a href="/billing" className="merchant-btn merchant-btn-primary">Configura Pagamento</a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sezione Account Google */}
                <div className="step-section">
                    <div className="step-header">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h2>Account Google</h2>
                            <p>Collega il tuo account Google per accedere ai servizi</p>
                        </div>
                    </div>
                    
                    <div className="step-card">
                        {isGoogleConnected ? (
                            <div className="step-complete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">✓</span>
                                        <span className="status-text">Collegato</span>
                                    </div>
                                    <div className="step-details">
                                        <div className="google-profile">
                                            {googleStatus.googleAccount.picture && (
                                                <img 
                                                    src={googleStatus.googleAccount.picture} 
                                                    alt="Google Profile" 
                                                    className="google-picture"
                                                />
                                            )}
                                            <div className="google-info">
                                                <strong>{googleStatus.googleAccount.name}</strong>
                                                <span>{googleStatus.googleAccount.email}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button 
                                    className="merchant-btn merchant-btn-outline"
                                    onClick={disconnectGoogle}
                                >
                                    Scollega
                                </button>
                            </div>
                        ) : (
                            <div className="step-incomplete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">!</span>
                                        <span className="status-text">Da collegare</span>
                                    </div>
                                    <div className="step-details">
                                        <span>Account Google richiesto per accedere ai servizi</span>
                                    </div>
                                </div>
                                <button 
                                    className="merchant-btn merchant-btn-primary"
                                    onClick={initiateGoogleAuth}
                                    disabled={connecting}
                                >
                                    {connecting ? 'Connessione...' : 'Collega Account Google'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sezione Google Ads */}
                <div className={`step-section ${!isGoogleConnected ? 'step-disabled' : ''}`}>
                    <div className="step-header">
                        <div className="step-number">4</div>
                        <div className="step-content">
                            <h2>Account Google Ads</h2>
                            <p>Collega il tuo account Google Ads per il monitoraggio</p>
                        </div>
                    </div>
                    
                    <div className="step-card">
                        {isGoogleAdsConnected ? (
                            <div className="step-complete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">✓</span>
                                        <span className="status-text">Collegato</span>
                                    </div>
                                    <div className="step-details">
                                        <strong>{googleStatus.googleAdsAccount.name}</strong>
                                        <span>ID: {googleStatus.googleAdsAccount.customerId}</span>
                                    </div>
                                </div>
                                <div className="step-actions">
                                    <button 
                                        className="merchant-btn merchant-btn-outline"
                                        onClick={loadGoogleAdsAccounts}
                                        disabled={connecting}
                                    >
                                        {connecting ? 'Caricamento...' : 'Modifica'}
                                    </button>
                                    <button 
                                        className="merchant-btn merchant-btn-outline merchant-btn-danger"
                                        onClick={disconnectGoogleAds}
                                    >
                                        Scollega
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="step-incomplete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">!</span>
                                        <span className="status-text">Da collegare</span>
                                    </div>
                                    <div className="step-details">
                                        <span>Account Google Ads richiesto per il monitoraggio della spesa</span>
                                    </div>
                                </div>
                                <button 
                                    className="merchant-btn merchant-btn-primary"
                                    onClick={loadGoogleAdsAccounts}
                                    disabled={!isGoogleConnected || connecting}
                                >
                                    {connecting ? 'Caricamento...' : 'Collega Google Ads'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Selezione Google Ads */}
                    {showGoogleAdsSelection && (
                        <div className="selection-modal">
                            <div className="selection-content">
                                <div className="selection-header">
                                    <h3>Seleziona Account Google Ads</h3>
                                    <button 
                                        onClick={() => {
                                            setShowGoogleAdsSelection(false);
                                            setSelectedGoogleAdsAccount(null);
                                            setGoogleAdsAccounts([]);
                                        }} 
                                        className="close-btn"
                                    >
                                        &times;
                                    </button>
                                </div>
                                
                                {googleAdsAccounts.length === 0 ? (
                                    <p>Nessun account Google Ads trovato.</p>
                                ) : (
                                    <>
                                        <div className="selection-hint">
                                            <strong>Seleziona l'account Google Ads da collegare</strong>
                                            <p>Questo account verrà utilizzato per recuperare i dati di spesa pubblicitaria.</p>
                                        </div>
                                        <div className="accounts-list">
                                            {googleAdsAccounts.map(account => (
                                                <div 
                                                    key={account.customerId} 
                                                    className={`account-item ${selectedGoogleAdsAccount?.customerId === account.customerId ? 'selected' : ''}`}
                                                    onClick={() => setSelectedGoogleAdsAccount(account)}
                                                >
                                                    <div className="account-info">
                                                        <h4>{account.name}</h4>
                                                        <p>ID: {account.customerId}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="selection-actions">
                                            <button 
                                                className="merchant-btn merchant-btn-outline"
                                                onClick={() => {
                                                    setShowGoogleAdsSelection(false);
                                                    setSelectedGoogleAdsAccount(null);
                                                    setGoogleAdsAccounts([]);
                                                }}
                                            >
                                                Annulla
                                            </button>
                                            <button 
                                                className="merchant-btn merchant-btn-primary"
                                                onClick={connectGoogleAds}
                                                disabled={!selectedGoogleAdsAccount || connecting}
                                            >
                                                {connecting ? 'Collegamento...' : 'Collega Google Ads'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sezione Merchant Center */}
                <div className={`step-section ${!isGoogleConnected || !isGoogleAdsConnected ? 'step-disabled' : ''}`}>
                    <div className="step-header">
                        <div className="step-number">5</div>
                        <div className="step-content">
                            <h2>Merchant Center</h2>
                            <p>Seleziona il merchant da trasferire a Loverlist CSS</p>
                        </div>
                    </div>
                    
                    <div className="step-card">
                        {isMerchantConnected ? (
                            <div className="step-complete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">✓</span>
                                        <span className="status-text">Collegato</span>
                                    </div>
                                    <div className="step-details">
                                        <strong>{googleStatus.merchantAccount.name}</strong>
                                        <span>ID: {googleStatus.merchantAccount.id}</span>
                                        {getStatusBadge(googleStatus.merchantAccount.status)}
                                    </div>
                                </div>
                                
                                <div className="step-actions">
                                    {googleStatus.merchantAccount.status === 'connected' && 
                                     isBillingComplete && isPaymentComplete && isGoogleConnected && isGoogleAdsConnected && (
                                        <>
                                            <div className="css-terms">
                                                <label className="checkbox-label">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={acceptCSSTerms}
                                                        onChange={(e) => setAcceptCSSTerms(e.target.checked)}
                                                    />
                                                    <span className="checkbox-text">
                                                        Accetto di corrispondere a Loverlist il 5% + IVA della mia spesa mensile 
                                                        degli ads shopping per utilizzare il CSS di Loverlist e risparmiare il 20% sui CPC
                                                    </span>
                                                </label>
                                            </div>
                                            <button 
                                                className="merchant-btn merchant-btn-primary"
                                                onClick={requestCSS}
                                                disabled={!acceptCSSTerms}
                                            >
                                                Richiedi passaggio a Loverlist CSS
                                            </button>
                                        </>
                                    )}
                                    
                                    {googleStatus.merchantAccount.status === 'requested' && (
                                        <div className="css-status">
                                            <h4>Richiesta inviata</h4>
                                            <p>La tua richiesta sarà gestita da un operatore entro le prossime 24 ore.</p>
                                        </div>
                                    )}
                                    
                                    {googleStatus.merchantAccount.status === 'css_request_sent' && (
                                        <div className="css-status">
                                            <h4>Richiesta inviata al merchant</h4>
                                            <p>Dopo aver approvato la richiesta su merchant, clicca su verifica.</p>
                                            <button 
                                                className="merchant-btn merchant-btn-primary"
                                                onClick={verifyCSSIntegration}
                                                disabled={connecting}
                                            >
                                                {connecting ? 'Verifica...' : 'Verifica Integrazione'}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {googleStatus.merchantAccount.status === 'accepted' && (
                                        <div className="css-success">
                                            <h4>CSS attivo</h4>
                                            <p>Stai utilizzando Loverlist CSS e risparmiando il 20% sui CPC!</p>
                                            {googleStatus.merchantAccount.approvedAt && (
                                                <p className="approved-date">
                                                    Attivato il: {new Date(googleStatus.merchantAccount.approvedAt).toLocaleDateString('it-IT')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    
                                    <button 
                                        className="merchant-btn merchant-btn-outline merchant-btn-danger"
                                        onClick={disconnectMerchant}
                                    >
                                        Scollega Merchant
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="step-incomplete">
                                <div className="step-info">
                                    <div className="step-status">
                                        <span className="status-icon">!</span>
                                        <span className="status-text">Da collegare</span>
                                    </div>
                                    <div className="step-details">
                                        <span>Merchant Center richiesto per il trasferimento a Loverlist CSS</span>
                                    </div>
                                </div>
                                <button 
                                    className="merchant-btn merchant-btn-primary"
                                    onClick={loadMerchantList}
                                    disabled={!isGoogleConnected || !isGoogleAdsConnected || connecting}
                                >
                                    {connecting ? 'Caricamento...' : 'Seleziona Merchant'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Selezione Merchant */}
                    {showMerchantSelection && (
                        <div className="selection-modal">
                            <div className="selection-content">
                                <div className="selection-header">
                                    <h3>Seleziona Merchant Center</h3>
                                    <button 
                                        onClick={() => {
                                            setShowMerchantSelection(false);
                                            setSelectedMerchant(null);
                                            setMerchants([]);
                                        }} 
                                        className="close-btn"
                                    >
                                        &times;
                                    </button>
                                </div>
                                
                                {merchants.length === 0 ? (
                                    <p>Nessun merchant trovato.</p>
                                ) : (
                                    <>
                                        <div className="selection-hint">
                                            <strong>Seleziona il merchant da collegare</strong>
                                            <p>Puoi collegare solo un merchant alla volta.</p>
                                        </div>
                                        <div className="accounts-list">
                                            {merchants.map(merchant => (
                                                <div 
                                                    key={merchant.id} 
                                                    className={`account-item ${selectedMerchant?.id === merchant.id ? 'selected' : ''}`}
                                                    onClick={() => setSelectedMerchant(merchant)}
                                                >
                                                    <div className="account-info">
                                                        <h4>{merchant.name}</h4>
                                                        <p>ID: {merchant.id}</p>
                                                        {String(merchant.id).length > 8 && (
                                                            <span className="subaccount-label">Subaccount gestito da MCA</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="selection-actions">
                                            <button 
                                                className="merchant-btn merchant-btn-outline"
                                                onClick={() => {
                                                    setShowMerchantSelection(false);
                                                    setSelectedMerchant(null);
                                                    setMerchants([]);
                                                }}
                                            >
                                                Annulla
                                            </button>
                                            <button 
                                                className="merchant-btn merchant-btn-primary"
                                                onClick={connectMerchant}
                                                disabled={!selectedMerchant || connecting}
                                            >
                                                {connecting ? 'Collegamento...' : 'Collega Merchant'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sezione Costi Campagne Shopping Esterne */}
            <div className="advertising-costs-section">
                <AdvertisingCostsTable
                    type="external"
                    title="Costi Campagne Shopping Esterne"
                    description="Costi delle campagne Shopping create sul tuo account Google Ads (fee 5% + IVA)"
                />
            </div>
        </div>
    );
};

export default MerchantPage; 